import React, { useState } from 'react';
import { FileText, Download, X, FileSpreadsheet, FileArchive, File as FileIcon } from 'lucide-react';
import { fileUrl, fmtSize } from '@/lib/upload';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

export const fileIconFor = (ct = '', name = '') => {
  const n = name.toLowerCase();
  if (ct.includes('pdf') || n.endsWith('.pdf')) return FileText;
  if (ct.includes('sheet') || ct.includes('csv') || /\.(xlsx?|csv)$/.test(n)) return FileSpreadsheet;
  if (ct.includes('zip') || n.endsWith('.zip')) return FileArchive;
  return FileIcon;
};

const norm = (a) => ({ ...a, original_filename: a.original_filename || a.name, is_image: a.is_image ?? (a.content_type || '').startsWith('image/') });

/** Renders attachment chips / inline images for a message. */
export const AttachmentList = ({ attachments = [], onRemove, compact = false }) => {
  const [preview, setPreview] = useState(null);
  if (!attachments.length) return null;
  const items = attachments.map(norm);
  const images = items.filter((a) => a.is_image);
  const files = items.filter((a) => !a.is_image);
  return (
    <div className="mt-1.5 space-y-1.5" data-testid="attachment-list">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {images.map((a) => (
            <div key={a.id} className="group/img relative">
              <button type="button" onClick={() => setPreview(a)} className={`block overflow-hidden rounded-xl border border-border bg-muted ${compact ? 'h-16 w-16' : 'max-h-64 max-w-xs'}`} data-testid={`attachment-image-${a.id}`}>
                <img src={fileUrl(a)} alt={a.original_filename} className={`${compact ? 'h-16 w-16 object-cover' : 'max-h-64 object-contain'}`} loading="lazy" />
              </button>
              {onRemove && <button type="button" onClick={() => onRemove(a)} className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 shadow border border-border text-muted-foreground hover:text-destructive" data-testid={`attachment-remove-${a.id}`}><X size={12} /></button>}
            </div>
          ))}
        </div>
      )}
      {files.map((a) => {
        const Icon = fileIconFor(a.content_type, a.original_filename);
        return (
          <div key={a.id} className="inline-flex max-w-full items-center gap-2 rounded-xl border border-border bg-white px-2.5 py-1.5 text-xs mr-1.5" data-testid={`attachment-file-${a.id}`}>
            <Icon size={16} className="shrink-0 text-primary" />
            <span className="truncate max-w-[180px] font-medium">{a.original_filename}</span>
            <span className="text-muted-foreground">{fmtSize(a.size)}</span>
            <a href={fileUrl(a, { download: true })} className="rounded-md p-0.5 text-muted-foreground hover:text-foreground" title="Download" data-testid={`attachment-download-${a.id}`}><Download size={13} /></a>
            {onRemove && <button type="button" onClick={() => onRemove(a)} className="rounded-md p-0.5 text-muted-foreground hover:text-destructive" data-testid={`attachment-remove-${a.id}`}><X size={13} /></button>}
          </div>
        );
      })}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl p-2 bg-black/90 border-0" data-testid="attachment-preview">
          <DialogTitle className="sr-only">{preview?.original_filename}</DialogTitle>
          {preview && <img src={fileUrl(preview)} alt={preview.original_filename} className="mx-auto max-h-[80vh] object-contain" />}
          {preview && <div className="flex items-center justify-between px-2 pt-1 text-xs text-white/80"><span className="truncate">{preview.original_filename}</span><a href={fileUrl(preview, { download: true })} className="inline-flex items-center gap-1 hover:text-white"><Download size={12} />Download</a></div>}
        </DialogContent>
      </Dialog>
    </div>
  );
};
